import React, { useState, useEffect, useRef } from "react";
import FileStatsChart from "../components/molecules/fileStatsChart";
import {
  deleteByDate,
  deleteByTeam,
  deleteByUser,
  searchByCourse,
  searchByDate,
  searchByMonth,
  searchByUsers,
  searchDeletedByUsers,
  searchDeletedByMonth,
  searchDeletedByDate,
  searchDeletedByCourse,
  restoreByTeam,
  restoreByUser,
  restoreByDate,
} from "../api/fileAPI";
import PerfectScrollbar from "react-perfect-scrollbar";
import { IoSearchSharp } from "react-icons/io5";
import SortButton from "../components/molecules/sortButton";
import FileDetailModal from "../components/molecules/fileDetailModal";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ko } from "date-fns/locale";

interface FileContent {
  totalSize: number;
  maxFileSize: number;
  totalFiles: number;
  userLastLoginDate?: number;
  userId?: string;
  nickName?: string;
  displayMonth?: string;
  displayDate?: string;
  courseId?: string;
  courseName?: string;
  totalTeams?: number;
  teamCreationDate?: number;
  startTime?: number;
  endTime?: number;
}

const titleRecord: Record<string, string> = {
  M: "월별",
  D: "일별",
  U: "사용자별",
  T: "팀별",
};

const dataKeyRecord: Record<string, string> = {
  M: "displayMonth",
  D: "displayDate",
  U: "nickName",
  T: "courseName",
};

function getTitle(key: string) {
  return titleRecord[key];
}

const FileSystem: React.FC = () => {
  const [fileInfo, setFileInfo] = useState<FileContent[]>([]);
  const [fliterFileInfo, setFliterFileInfo] = useState<FileContent[]>([]);
  const [fileSubject, setFileSubject] = useState<string>("M"); // M = 월별, D = 일별, U = 사용자별, T = 팀별
  const [fileViewMode, setFileViewMode] = useState<"active" | "deleted">("active"); // 일반 파일 / 삭제된 파일

  // 기간 필터 상태
  const [yearRange, setYearRange] = useState<[Date | null, Date | null]>([null, null]);
  const [monthRange, setMonthRange] = useState<[Date | null, Date | null]>([null, null]);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [lastLoginDateRange, setLastLoginDateRange] = useState<[Date | null, Date | null]>([null, null]);

  const searchFileSelect = async (sub: string) => {
    // 파일 보기 모드에 따라 다른 API 호출
    if (fileViewMode === "deleted") {
      if (sub === "M") {
        return await searchDeletedByMonth(0, 1000, `${dataKeyRecord[sub]},asc`);
      } else if (sub === "D") {
        return await searchDeletedByDate(0, 1000, `${dataKeyRecord[sub]},asc`);
      } else if (sub === "U") {
        return await searchDeletedByUsers(0, 1000, `nickName,asc`);
      } else if (sub === "T") {
        return await searchDeletedByCourse(0, 1000, `${dataKeyRecord[sub]},asc`);
      }
    } else {
      if (sub === "M") {
        return await searchByMonth(0, 1000, `${dataKeyRecord[sub]},asc`);
      } else if (sub === "D") {
        return await searchByDate(0, 1000, `${dataKeyRecord[sub]},asc`);
      } else if (sub === "U") {
        return await searchByUsers(0, 1000, `nickName,asc`);
      } else if (sub === "T") {
        return await searchByCourse(0, 1000, `${dataKeyRecord[sub]},asc`);
      }
    }
  };

  // 통계 선택한 항목 별로 삭제 api 구분분
  const deleteFileSelect = async () => {
    if (selectedData) {
      if (fileSubject === "M" || fileSubject === "D") {
        return await deleteByDate(
          selectedData.map((data) =>
            data.startTime && data.endTime
              ? { startTime: data.startTime, endTime: data.endTime }
              : { startTime: 0, endTime: 0 }
          )
        );
      } else if (fileSubject === "U") {
        return await deleteByUser(selectedData.map((data) => (data.userId ? data.userId : "")));
      } else if (fileSubject === "T") {
        return await deleteByTeam(selectedData.map((data) => (data.courseId ? data.courseId : "")));
      }
    }
  };

  // 통계 선택한 항목 별로 복원 api 구분
  const restoreFileSelect = async () => {
    if (selectedData) {
      if (fileSubject === "M" || fileSubject === "D") {
        return await restoreByDate(
          selectedData.map((data) =>
            data.startTime && data.endTime
              ? { startTime: data.startTime, endTime: data.endTime }
              : { startTime: 0, endTime: 0 }
          )
        );
      } else if (fileSubject === "U") {
        return await restoreByUser(selectedData.map((data) => (data.userId ? data.userId : "")));
      } else if (fileSubject === "T") {
        return await restoreByTeam(selectedData.map((data) => (data.courseId ? data.courseId : "")));
      }
    }
  };

  // 파일 삭제
  const deleteFile = async () => {
    const data = await deleteFileSelect();
    if (data) {
      console.log(data.data);
      searchFile(fileSubject);
      deleteAccessAlert();
    }
  };

  // 파일 복원
  const restoreFile = async () => {
    const data = await restoreFileSelect();
    if (data) {
      console.log(data.data);
      searchFile(fileSubject);
      restoreAccessAlert();
    }
  };

  // 삭제 경고창
  const handleDeleteConfirm = () => {
    Swal.fire({
      title: `정말 선택한 데이터들을 \n삭제하시겠습니까??`,
      text: `총 ${(selectedData.reduce((acc, file) => acc + file.totalSize, 0) / 1024).toFixed(2)} GB, \n      ${selectedData.reduce((acc, file) => acc + file.totalFiles, 0).toLocaleString()}개의 파일입니다.`,
      icon: "warning",
      showCancelButton: true, // '아니오' 버튼 추가
      color: "red",
      confirmButtonColor: "#3085d6", // '예' 버튼 색상
      cancelButtonColor: "#d33", // '아니오' 버튼 색상
      confirmButtonText: "예",
      cancelButtonText: "아니오",
    }).then((result) => {
      if (result.isConfirmed) {
        // '예'를 선택한 경우 실행할 함수
        deleteFile();
      } else {
        // '아니오'를 선택한 경우 실행할 코드
      }
    });
  };

  // 삭제 성공 알림
  const deleteAccessAlert = () => {
    Swal.fire({
      title: "성공",
      text: "삭제가 성공적으로 완료되었습니다.",
      icon: "success", // success, error, warning, info, question 중 선택 가능
      confirmButtonText: "확인",
      timer: 3000, // 알림이 자동으로 사라지는 시간 (ms)
      // timerProgressBar: true, // 타이머 진행 상태를 표시
    });
  };

  // 복원 경고창
  const handleRestoreConfirm = () => {
    Swal.fire({
      title: `정말 선택한 데이터들을 \n복원하시겠습니까??`,
      text: `총 ${(selectedData.reduce((acc, file) => acc + file.totalSize, 0) / 1024).toFixed(2)} GB, \n      ${selectedData.reduce((acc, file) => acc + file.totalFiles, 0).toLocaleString()}개의 파일입니다.`,
      icon: "warning",
      showCancelButton: true,
      color: "blue",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "예",
      cancelButtonText: "아니오",
    }).then((result) => {
      if (result.isConfirmed) {
        restoreFile();
      }
    });
  };

  // 복원 성공 알림
  const restoreAccessAlert = () => {
    Swal.fire({
      title: "성공",
      text: "복원이 성공적으로 완료되었습니다.",
      icon: "success",
      confirmButtonText: "확인",
      timer: 3000,
    });
  };

  const formatMB = (bytes: number) => +(bytes / 1024 / 1024).toFixed(2);

  const formatTimestamp = (timestamp: number | undefined) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const searchFile = async (sub: string) => {
    const data = await searchFileSelect(sub);
    if (data) {
      const formattedData = data.data.content.map((info: FileContent) => ({
        ...info,
        totalSize: formatMB(info.totalSize),
        maxFileSize: formatMB(info.maxFileSize),
      }));
      setFileInfo(formattedData);
      setSelectedRow(-1);
      setSortState("");
      setSelectedData([]);
      console.log(data.data);
    }
  };

  // 데이터 로딩 (탭 변경 시)
  useEffect(() => {
    searchFile(fileSubject);
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollTop = 0;
    }
  }, [fileSubject, fileViewMode]);

  // 오른쪽 컨테이너
  const [keyword, setKeyword] = useState<string>("");
  const scrollbarRef = useRef<HTMLElement | null>(null);

  // 클라이언트 사이드 필터링 및 검색
  useEffect(() => {
    let filteredData = [...fileInfo];

    // 기간 필터링
    if (fileSubject === "M") {
      const [startYear, endYear] = yearRange;
      if (startYear && endYear) {
        filteredData = filteredData.filter((file) => {
          if (!file.displayMonth) return false;
          const fileYear = parseInt(file.displayMonth.substring(0, 4), 10);
          return fileYear >= startYear.getFullYear() && fileYear <= endYear.getFullYear();
        });
      }
    } else if (fileSubject === "D") {
      const [startMonth, endMonth] = monthRange;
      if (startMonth && endMonth) {
        const start = new Date(startMonth.getFullYear(), startMonth.getMonth(), 1);
        const end = new Date(endMonth.getFullYear(), endMonth.getMonth() + 1, 0);
        filteredData = filteredData.filter((file) => {
          if (!file.displayDate) return false;
          const fileDate = new Date(file.displayDate);
          return fileDate >= start && fileDate <= end;
        });
      }
    } else if (fileSubject === "T") {
      const [startDate, endDate] = dateRange;
      if (startDate && endDate) {
        const start = startDate.getTime();
        const end = endDate.getTime();
        filteredData = filteredData.filter(
          (file) => file.teamCreationDate && file.teamCreationDate >= start && file.teamCreationDate <= end
        );
      }
    } else if (fileSubject === "U") {
      const [startDate, endDate] = lastLoginDateRange;
      if (startDate && endDate) {
        const start = startDate.getTime();
        const end = endDate.getTime();
        filteredData = filteredData.filter(
          (file) => file.userLastLoginDate && file.userLastLoginDate >= start && file.userLastLoginDate <= end
        );
      }
    }

    // 키워드 검색
    if (keyword) {
      filteredData = filteredData.filter((file) =>
        file[dataKeyRecord[fileSubject]]?.toLowerCase().includes(keyword.toLowerCase())
      );
    }

    setFliterFileInfo(filteredData);
    setSelectedRow(-1);
  }, [fileInfo, keyword, yearRange, monthRange, dateRange, lastLoginDateRange, fileSubject]);

  // 정렬 기준
  const [sortState, setSortState] = useState<string>("");

  const handleSortState = (state: string) => {
    setSortState(state);
  };

  type SortDirection = "asc" | "desc";
  function sortByUniversal<T>(key: keyof T, direction: SortDirection = "asc") {
    return (a: T, b: T) => {
      const aVal = a[key];
      const bVal = b[key];

      if (aVal == null && bVal != null) return direction === "asc" ? 1 : -1;
      if (bVal == null && aVal != null) return direction === "asc" ? -1 : 1;
      if (aVal == null && bVal == null) return 0;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }

      if (aVal instanceof Date || bVal instanceof Date) {
        const aTime = new Date(aVal as any).getTime();
        const bTime = new Date(bVal as any).getTime();
        return direction === "asc" ? aTime - bTime : bTime - aTime;
      }

      return direction === "asc" ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
    };
  }

  // 정렬 적용
  useEffect(() => {
    if (sortState) {
      const [sortKey, sortDirection] = sortState.split(",") as [keyof FileContent, SortDirection];
      const sortedData = [...fliterFileInfo].sort(sortByUniversal(sortKey, sortDirection));
      setFliterFileInfo(sortedData);
    }
  }, [sortState]);

  // 클릭 표시
  const [selectedRow, setSelectedRow] = useState<number>(-1);

  const handleSelectedRow = (id: number) => {
    setSelectedRow(id);
  };

  // 체크박스
  const [selectedData, setSelectedData] = useState<FileContent[]>([]);

  const handleAllCheck = () => {
    setSelectedData([]);
  };

  // 데이터 선택 처리 함수
  const choiceData = (info: FileContent) => {
    if (selectedData.includes(info)) {
      setSelectedData(selectedData.filter((data) => data !== info));
    } else {
      setSelectedData([...selectedData, info]);
    }
  };

  // 디테일 모달
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);
  const [propData, setPropData] = useState<FileContent | null>(null);

  useEffect(() => {
    if (!isDetailModalOpen) {
      setPropData(null);
      searchFile(fileSubject);
    }
  }, [isDetailModalOpen]);

  const handleTabClick = (key: string) => {
    setFileSubject(key);
    // 탭 변경 시 필터 초기화
    setYearRange([null, null]);
    setMonthRange([null, null]);
    setDateRange([null, null]);
    setLastLoginDateRange([null, null]);
    setKeyword("");
    setSelectedData([]);
  };

  return (
    <div>
      <div className="max-w-[1920px] mx-auto sm:px-2 lg:px-4 py-2">
        <div className="flex gap-6">
          <div className="min-w-[52vw] max-w-[52vw] bg-white rounded-lg shadow-sm p-4">
            <FileStatsChart
              title={`${fileViewMode === "deleted" ? "삭제된 " : ""}${getTitle(fileSubject)} 파일 통계`}
              datakey={dataKeyRecord[fileSubject]}
              data={fliterFileInfo} // 차트에도 필터링된 데이터 사용
              selectedData={selectedData}
            />
          </div>

          <div className="min-w-[44vw] max-w-[44vw] bg-white rounded-lg shadow-sm p-4">
            <div className="border-b border-gray-400">
              <nav className="flex justify-between">
                <div>
                  {Object.entries(titleRecord).map(([key, value]) => (
                    <button
                      key={key}
                      className={`px-3 py-2 text-sm font-medium  rounded-t-lg ${
                        key === fileSubject
                          ? "border-gray-400 border shadow-md"
                          : "text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:border"
                      }`}
                      onClick={() => handleTabClick(key)}
                    >
                      {value}
                    </button>
                  ))}
                </div>
                <div>
                  <button
                    className="px-3 py-[6px] border border-gray-500 rounded-md hover:bg-gray-100"
                    onClick={() => {
                      setIsDetailModalOpen(true);
                    }}
                  >
                    파일 상세 관리
                  </button>
                </div>
              </nav>
            </div>

            {/* 검색 및 필터 */}
            <div className="mt-4">
              <div className="mb-4">
                <div className="flex flex-col mb-2 space-y-3">
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="검색"
                          value={keyword}
                          onChange={(e) => setKeyword(e.target.value)}
                          className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-md"
                        />

                        <span className="absolute text-gray-400 left-3 top-3">
                          <IoSearchSharp size={20} />
                        </span>
                      </div>
                    </div>
                    <div className="w-48">
                      <select
                        value={fileViewMode}
                        onChange={(e) => {
                          setFileViewMode(e.target.value as "active" | "deleted");
                          setSelectedData([]);
                          setSelectedRow(-1);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="active">일반 파일</option>
                        <option value="deleted">삭제된 파일</option>
                      </select>
                    </div>
                  </div>

                  {/* 기간 필터 */}
                  <div className="flex items-center space-x-2">
                    {fileSubject === "M" && (
                      <>
                        <span className="text-sm text-gray-500">연도 범위:</span>
                        <DatePicker
                          selected={yearRange[0]}
                          onChange={(date) => setYearRange([date, yearRange[1]])}
                          selectsStart
                          startDate={yearRange[0]}
                          endDate={yearRange[1]}
                          showYearPicker
                          dateFormat="yyyy"
                          className="w-28 px-3 py-2 border border-gray-200 rounded-lg"
                          locale={ko}
                          placeholderText="시작 연도"
                        />
                        <span className="text-gray-500">~</span>
                        <DatePicker
                          selected={yearRange[1]}
                          onChange={(date) => setYearRange([yearRange[0], date])}
                          selectsEnd
                          startDate={yearRange[0]}
                          endDate={yearRange[1]}
                          minDate={yearRange[0]}
                          showYearPicker
                          dateFormat="yyyy"
                          className="w-28 px-3 py-2 border border-gray-200 rounded-lg"
                          locale={ko}
                          placeholderText="종료 연도"
                        />
                      </>
                    )}
                    {fileSubject === "D" && (
                      <>
                        <span className="text-sm text-gray-500">월 범위:</span>
                        <DatePicker
                          selected={monthRange[0]}
                          onChange={(date) => setMonthRange([date, monthRange[1]])}
                          selectsStart
                          startDate={monthRange[0]}
                          endDate={monthRange[1]}
                          showMonthYearPicker
                          dateFormat="yyyy-MM"
                          className="w-32 px-3 py-2 border border-gray-200 rounded-lg"
                          locale={ko}
                          placeholderText="시작 월"
                        />
                        <span className="text-gray-500">~</span>
                        <DatePicker
                          selected={monthRange[1]}
                          onChange={(date) => setMonthRange([monthRange[0], date])}
                          selectsEnd
                          startDate={monthRange[0]}
                          endDate={monthRange[1]}
                          minDate={monthRange[0]}
                          showMonthYearPicker
                          dateFormat="yyyy-MM"
                          className="w-32 px-3 py-2 border border-gray-200 rounded-lg"
                          locale={ko}
                          placeholderText="종료 월"
                        />
                      </>
                    )}
                    {fileSubject === "T" && (
                      <>
                        <span className="text-sm text-gray-500">팀 생성 기간:</span>
                        <DatePicker
                          selected={dateRange[0]}
                          onChange={(date) => setDateRange([date, dateRange[1]])}
                          selectsStart
                          startDate={dateRange[0]}
                          endDate={dateRange[1]}
                          dateFormat="yyyy-MM-dd"
                          className="w-32 px-3 py-2 border border-gray-200 rounded-lg"
                          locale={ko}
                          placeholderText="시작일"
                        />
                        <span className="text-gray-500">~</span>
                        <DatePicker
                          selected={dateRange[1]}
                          onChange={(date) => setDateRange([dateRange[0], date])}
                          selectsEnd
                          startDate={dateRange[0]}
                          endDate={dateRange[1]}
                          minDate={dateRange[0]}
                          dateFormat="yyyy-MM-dd"
                          className="w-32 px-3 py-2 border border-gray-200 rounded-lg"
                          locale={ko}
                          placeholderText="종료일"
                        />
                      </>
                    )}
                    {fileSubject === "U" && (
                      <>
                        <span className="text-sm text-gray-500">최종 접속일:</span>
                        <DatePicker
                          selected={lastLoginDateRange[0]}
                          onChange={(date) => setLastLoginDateRange([date, lastLoginDateRange[1]])}
                          selectsStart
                          startDate={lastLoginDateRange[0]}
                          endDate={lastLoginDateRange[1]}
                          dateFormat="yyyy-MM-dd"
                          className="w-32 px-3 py-2 border border-gray-200 rounded-lg"
                          locale={ko}
                          placeholderText="시작일"
                        />
                        <span className="text-gray-500">~</span>
                        <DatePicker
                          selected={lastLoginDateRange[1]}
                          onChange={(date) => setLastLoginDateRange([lastLoginDateRange[0], date])}
                          selectsEnd
                          startDate={lastLoginDateRange[0]}
                          endDate={lastLoginDateRange[1]}
                          minDate={lastLoginDateRange[0]}
                          dateFormat="yyyy-MM-dd"
                          className="w-32 px-3 py-2 border border-gray-200 rounded-lg"
                          locale={ko}
                          placeholderText="종료일"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between mb-0">
                <span className="text-sm text-gray-600">총 {fliterFileInfo.length}건</span>
                <div className="flex items-center">
                  {selectedData.length > 0 && (
                    <span className="mr-4 text-sm text-blue-600">
                      {selectedData.length}개 항목 선택 (총{" "}
                      {selectedData
                        .reduce((acc, file) => acc + file.totalSize, 0)
                        .toLocaleString(undefined, { maximumFractionDigits: 2 })}{" "}
                      MB)
                    </span>
                  )}
                  {fileViewMode === "deleted" ? (
                    <button
                      className={`px-4 py-1 mb-1 rounded-md ${
                        selectedData.length === 0
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "text-white bg-blue-500 hover:bg-blue-400"
                      }`}
                      onClick={handleRestoreConfirm}
                      disabled={selectedData.length === 0}
                    >
                      복원
                    </button>
                  ) : (
                    <button
                      className={`px-4 py-1 mb-1 rounded-md ${
                        selectedData.length === 0
                          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                          : "text-gray-100 bg-red-500 hover:bg-red-300"
                      }`}
                      onClick={handleDeleteConfirm}
                      disabled={selectedData.length === 0}
                    >
                      삭제
                    </button>
                  )}
                </div>
              </div>

              {/* 표 보여주는 것 */}
              <div>
                <PerfectScrollbar containerRef={(ref) => (scrollbarRef.current = ref)}>
                  <div className="min-h-[650px] max-h-[650px]">
                    <table className="min-w-full border-collapse divide-y divide-gray-200">
                      <thead className="sticky top-0 bg-gray-50">
                        <tr className="border-b border-gray-200">
                          <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                            <input
                              type="checkbox"
                              className="border-gray-300 rounded"
                              checked={selectedData.length > 0}
                              disabled={selectedData.length <= 0}
                              onChange={() => {
                                handleAllCheck();
                              }}
                            />
                          </th>
                          <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                            <SortButton
                              title={titleRecord[fileSubject]}
                              sortState={sortState}
                              sortList={["", `${dataKeyRecord[fileSubject]},asc`, `${dataKeyRecord[fileSubject]},desc`]}
                              handleState={handleSortState}
                            />
                          </th>
                          {fileSubject === "U" && (
                            <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                              <SortButton
                                title="최종 접속일"
                                sortState={sortState}
                                sortList={["", "userLastLoginDate,asc", "userLastLoginDate,desc"]}
                                handleState={handleSortState}
                              />
                            </th>
                          )}
                          {fileSubject === "T" && (
                            <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                              <SortButton
                                title="팀 생성일"
                                sortState={sortState}
                                sortList={["", "teamCreationDate,asc", "teamCreationDate,desc"]}
                                handleState={handleSortState}
                              />
                            </th>
                          )}
                          <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                            <SortButton
                              title="전체 파일 크기"
                              sortState={sortState}
                              sortList={["", "totalSize,asc", "totalSize,desc"]}
                              handleState={handleSortState}
                            />
                          </th>
                          <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                            <SortButton
                              title="가장 큰 파일"
                              sortState={sortState}
                              sortList={["", "maxFileSize,asc", "maxFileSize,desc"]}
                              handleState={handleSortState}
                            />
                          </th>
                          <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-x-2">
                            <SortButton
                              title="총 파일 개수"
                              sortState={sortState}
                              sortList={["", "totalFiles,asc", "totalFiles,desc"]}
                              handleState={handleSortState}
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200 round">
                        {fliterFileInfo.map((info, idx) => (
                          <tr
                            className={`${selectedRow === idx ? "bg-orange-100" : ""}`}
                            key={idx}
                            onClick={() => {
                              if (selectedRow === idx) {
                                handleSelectedRow(-1);
                              } else {
                                handleSelectedRow(idx);
                              }
                            }}
                          >
                            <td className="px-3 py-2 whitespace-nowrap">
                              <input
                                type="checkbox"
                                className="border-gray-300 rounded hover:cursor-pointer"
                                checked={selectedData.includes(info)}
                                onChange={() => {
                                  choiceData(info);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              />
                            </td>
                            <td className="px-3 py-2 min-w-[12vw] max-w-[12vw] border-x-2">
                              <div
                                className="text-sm text-gray-900 truncate hover:cursor-pointer hover:text-blue-500 hover:underline"
                                onClick={() => {
                                  setIsDetailModalOpen(true);
                                  setPropData(info);
                                }}
                              >
                                {info[dataKeyRecord[fileSubject]] ? info[dataKeyRecord[fileSubject]] : "[익명 유저]"}
                              </div>
                            </td>
                            {fileSubject === "U" && (
                              <td className="px-3 py-2 min-w-[10vw] max-w-[10vw] border-x-2">
                                <div className="text-sm text-gray-900 truncate">
                                  {formatTimestamp(info.userLastLoginDate)}
                                </div>
                              </td>
                            )}
                            {fileSubject === "T" && (
                              <td className="px-3 py-2 min-w-[10vw] max-w-[10vw] border-x-2">
                                <div className="text-sm text-gray-900 truncate">
                                  {formatTimestamp(info.teamCreationDate)}
                                </div>
                              </td>
                            )}
                            <td className="px-3 py-2 min-w-[5vw] max-w-[5vw] border-x-2 text-end">
                              <div className="text-sm text-gray-900 truncate">{info.totalSize.toLocaleString()} MB</div>
                            </td>
                            <td className="px-3 py-2 min-w-[3vw] max-w-[3vw] border-x-2 text-end">
                              <div className="text-sm text-gray-900 truncate">
                                {info.maxFileSize.toLocaleString()} MB
                              </div>
                            </td>
                            <td className="px-3 py-2 min-w-[5vw] max-w-[5vw] border-x-2 text-end">
                              <div className="text-sm text-gray-900 truncate">
                                {info.totalFiles.toLocaleString()} 개
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </PerfectScrollbar>
              </div>
            </div>
          </div>
        </div>

        <FileDetailModal isOpen={isDetailModalOpen} onClose={setIsDetailModalOpen} propData={propData} />
      </div>
    </div>
  );
};

export default FileSystem;
